import LoggingBase from '../../../../base/loggingbase';
import Validator from '../../../../utils/Validator';
import AutoFitModeExtension from './AutoFitModeExtension';

/**
 * class AutoFitModeManager - drives the "auto-fit mode" column width management for the
 * host table ({@link XtwTbl}). It is a plain delegate: the host is passed in and accessed
 * via {@link #host}. It owns the {@link AutoFitModeExtension} that performs the actual
 * column computations.
 */
export default class AutoFitModeManager extends LoggingBase {

	/**
	 * constructs a new instance
	 * @param {*} host the host table (XtwTbl)
	 * @param {Boolean} isAutoFitModeOn whether the auto-fit mode is enabled
	 */
	constructor( host, isAutoFitModeOn = false ) {
		super( 'widgets.xtw.autofit.AutoFitModeManager' );
		this._host = host;
		this._afmExt = new AutoFitModeExtension( host, isAutoFitModeOn );
	}

	/**
	 * @returns {*} the host table
	 */
	get host() {
		return this._host;
	}

	/**
	 * @returns {AutoFitModeExtension} the auto-fit mode extension
	 */
	get autoFitModeExtension() {
		return this._afmExt;
	}

	/**
	 * @override
	 */
	doDestroy() {
		if ( this._afmExt ) {
			this._afmExt.destroy();
			this._afmExt = null;
		}
		this._host = void 0;
		super.doDestroy();
	}

	getColumn( columnId ) {
		const head = this.host.wdgHead;
		return Validator.isObject( head ) ? head.getColumn( columnId ) : void 0;
	}

	setColumnWidth( columnId, width ) {
		const host = this.host;
		if ( !host.ready || !host.alive || !Validator.isObject( host.wdgHead ) ) {
			return false;
		}
		host.wdgHead.set_width( { idc: columnId, width: width } );
		return true;
	}

	autoFitColumns() {
		const host = this.host;
		if ( !host.ready || !host.alive ) {
			return false;
		}
		const parent = host.parent;
		if ( !Validator.isObject( parent ) || !Validator.isFunction( parent.getClientArea ) ) {
			return this.autoFitColumnsBasedOnHeaderClientRect();
		}
		const parentClientArea = parent.getClientArea();
		if ( !Validator.isArray( parentClientArea ) || parentClientArea.length < 3 ) {
			return this.autoFitColumnsBasedOnHeaderClientRect();
		}
		const headerWidth = parentClientArea[ 2 ];
		if ( !Validator.isPositiveNumber( headerWidth ) ) {
			return this.autoFitColumnsBasedOnHeaderClientRect();
		}
		return this.setAutoFitModusColumnsWidths( headerWidth );
	}

	autoFitColumnsBasedOnHeaderClientRect() {
		const host = this.host;
		if ( !host.ready || !host.alive || !Validator.isObject( host.wdgHead ) ) {
			return false;
		}
		const headerWidth = host.wdgHead.headClientWidth;
		if ( !Validator.isPositiveNumber( headerWidth ) ) {
			return false;
		}
		return this.setAutoFitModusColumnsWidths( headerWidth );
	}

	setAutoFitModusColumnsWidths( headerWidth ) {
		const host = this.host;
		if ( !host.ready || !host.alive ||
			!Validator.isPositiveNumber( headerWidth, false ) ) {
			return false;
		}
		const autoFitAdmin = this._afmExt;
		if ( !autoFitAdmin.shouldAutoFit ) {
			return false;
		}
		let availableWidth = headerWidth;
		const selectionColumnWidth = autoFitAdmin.selectionColumnWidth;
		if ( Validator.isPositiveNumber( selectionColumnWidth, false ) ) {
			availableWidth -= selectionColumnWidth;
		}
		const verticalSliderWidth = autoFitAdmin.verticalSliderWidth;
		if ( Validator.isPositiveNumber( verticalSliderWidth, false ) ) {
			availableWidth -= verticalSliderWidth;
		}
		if ( !Validator.isPositiveNumber( availableWidth, false ) ) {
			return false;
		}
		const columnsAutoFitProperties = autoFitAdmin.columnsAutoFitProperties;
		if ( !Validator.isMap( columnsAutoFitProperties, true ) ) {
			return false;
		}
		let allColumnsWidthsSet = true;
		for ( let columnId of [ ...columnsAutoFitProperties.keys() ] ) {
			const properties = columnsAutoFitProperties.get( columnId );
			if ( this.setColumnWidth( columnId, properties.quota * availableWidth ) ) {
				continue;
			}
			allColumnsWidthsSet = false;
		}
		this.syncServerColumnsWidths();
		return allColumnsWidthsSet;
	}

	resizeColumnOnAutoFit( column, newWidth ) {
		const host = this.host;
		if ( !host.ready || !host.alive || !Validator.isObject( column ) || !Validator.isPositiveNumber( newWidth ) ) {
			return false;
		}
		const autoFitAdmin = this._afmExt;
		if ( !autoFitAdmin.shouldAutoFit ) {
			return false;
		}
		newWidth = Math.round( newWidth ); // only integer widths
		if ( newWidth < host.minimalColumnWidth ) {
			// TODO log
			// we are returning true even though the operation was not successfull,
			// in order to prevent the standard/traditional column resizing from
			// happening (the one that is not aware of auo-fit)
			return true;
		}
		const oldWidth = column.width;
		// TODO should we compare "integer" values by rounding them?
		if ( !Validator.isPositiveNumber( oldWidth ) || newWidth == oldWidth ) {
			// TODO log
			// we are returning true even though the operation was not successfull,
			// in order to prevent the standard/traditional column resizing from
			// happening (the one that is not aware of auo-fit)
			return true;
		}
		const visibleColumns = autoFitAdmin.visibleColumns;
		if ( Validator.isArray( visibleColumns ) && visibleColumns.length == 1 ) {
			// so in this case, the column that should be resized is the only
			// visible column and should take all the available space; we should
			// not allow any "manual" width changes to happen, but we might have to
			// make sure that the column ACTUALLY takes all the available space by
			// setting its width to the available width; in any case we should not
			// allow the "manually" set width to be processed by the web server
			return true;
		}
		const neighbourColumn = autoFitAdmin.getNeighbourColumn( column, true );
		if ( !Validator.isObject( neighbourColumn ) ||
			!Validator.isPositiveNumber( neighbourColumn.width ) ) {
			// TODO log
			// we are returning true even though the operation was not successfull,
			// in order to prevent the standard/traditional column resizing from
			// happening (the one that is not aware of auo-fit)
			return true;
		}
		const neighbourColumnsWidthChange = oldWidth - newWidth;
		if ( neighbourColumn.width + neighbourColumnsWidthChange < host.minimalColumnWidth ) {
			// TODO log
			// we are returning true even though the operation was not successfull,
			// in order to prevent the standard/traditional column resizing from
			// happening (the one that is not aware of auo-fit)
			return true;
		}
		if ( !this.setColumnWidth( column.id, newWidth ) ) {
			// TODO log
			// we are returning true even though the operation was not successfull,
			// in order to prevent the standard/traditional column resizing from
			// happening (the one that is not aware of auo-fit)
			return true;
		}
		if ( !this.setColumnWidth( neighbourColumn.id,
				neighbourColumn.width + neighbourColumnsWidthChange ) ) {
			// revert changes
			this.setColumnWidth( column.id, oldWidth );
			// TODO log
			// we are returning true even though the operation was not successfull,
			// in order to prevent the standard/traditional column resizing from
			// happening (the one that is not aware of auo-fit)
			return true;
		}
		this.syncServerColumnsWidths();
		return true;
	}

	reactToColumnVisibilityChange( alreadyChangedColumn ) {
		const host = this.host;
		if ( !host.ready || !host.alive ) {
			return;
		}
		this.autoFitColumns();
	}

	/**
	 * inform the web server about the current columns widths, without triggering
	 * an update on the web-server part
	 */
	syncServerColumnsWidths() {
		const columnsAutoFitProperties = this._afmExt.columnsAutoFitProperties;
		if ( !Validator.isMap( columnsAutoFitProperties, true ) ) {
			return false;
		}
		const columnsWidths = {};
		for ( let columnId of [ ...columnsAutoFitProperties.keys() ] ) {
			const properties = columnsAutoFitProperties.get( columnId );
			columnsWidths[ String( columnId ) ] = Math.round( properties.definedWidth );
		}
		this.host._nfySrv( "columnsWidths", columnsWidths );
		return true;
	}

}
